import axios, { AxiosResponse } from "axios";
import { useLayoutEffect } from "react";
import { baseUrlApi } from "../proxy";
import {
  applyThemeColor,
  DEFAULT_THEME_COLOR,
  extractPortalTheme,
  THEME_CACHE_KEY,
  themeIsActive,
} from "./theme";

type CachedTheme = { date: string; color: string };
let themeRequest: Promise<AxiosResponse<unknown>> | null = null;

const todayKey = () => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const readTodayTheme = (): CachedTheme | null => {
  try {
    const cached = JSON.parse(localStorage.getItem(THEME_CACHE_KEY) || "null");
    return cached?.date === todayKey() && typeof cached.color === "string" ? cached : null;
  } catch {
    return null;
  }
};

/** Loads the active identity once per application start and keeps a same-day cache for first paint. */
export default function ThemeLoader() {
  useLayoutEffect(() => {
    let disposed = false;
    const cached = readTodayTheme();
    applyThemeColor(cached?.color || DEFAULT_THEME_COLOR);

    themeRequest ??= axios.get(`${baseUrlApi}portal/theme`);
    themeRequest
      .then((response) => {
        if (disposed) return;
        const theme = extractPortalTheme(response.data);
        const color = themeIsActive(theme) ? theme?.color : DEFAULT_THEME_COLOR;
        applyThemeColor(color);
        localStorage.setItem(
          THEME_CACHE_KEY,
          JSON.stringify({ date: todayKey(), color: color || DEFAULT_THEME_COLOR })
        );
      })
      .catch(() => {
        if (!disposed) applyThemeColor(cached?.color || DEFAULT_THEME_COLOR);
      })
      .finally(() => {
        if (!disposed) document.documentElement.classList.add("ac-theme-ready");
      });

    return () => {
      disposed = true;
    };
  }, []);

  return null;
}
